<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';

	import { home_data } from 'src/stores/account/user';
import { app, os } from 'src/stores/session/native-bridge';
import { isRateUsClicked } from 'src/stores/ui/promotions';
	import { capFirstChar } from 'src/helpers/utilities';
	import { showRatingModal } from 'src/stores/ui/on-log-modals';

	import { handleUserGivenRating, postReq } from 'src/services/http';
	import { open_external_link } from 'src/services/native_container_requests_ios';
	import {
		open_external_link_android,
		showAndroidRateAppPopUp,
	} from 'src/services/native_container_requests_android';

	// Design System components
	import {
		Text,
		Button,
		Modal,
		Container,
		Rating,
		Textarea,
		Icon,
	} from '@Shelly/shelly-design-system';
	import { mobile } from 'src/services/mobile-rework';
	import { NativeOperationSystem } from 'src/services/init/types/init-type';
	import { get } from 'svelte/store';
	import { modalStore } from 'src/stores/ui/modal_states';
	// import TransitionWrapper from './Utility/TransitionWrapper.svelte';

	export let showModal = false;
	export let campaignId: string | undefined;
	export let priority = 30;

	const dispatch = createEventDispatcher();

	let submittedRating = false;
	let isSubmittedRatingLoading = false;
	let rating = 0;
	let textareaValue = '';
	let platform = $os == 'IOS' ? 'ios' : 'android';

	const onCloseRatingModal = () => {
		isRateUsClicked.set(false);
		dispatch('close');
		if (!submittedRating) {
			const res = handleUserGivenRating({
				[platform]: {
					show: true,
					condition: 'premium',
					ts: Date.now(),
					userAction: 'later',
				},
			});
		}
	};

	const dontAskAgain = () => {
		handleUserGivenRating({
			[platform]: {
				show: false,
				condition: 'premium',
				ts: Date.now(),
				userAction: 'dontask',
			},
		});
		updateHomeData('dontask');
		isRateUsClicked.set(false);
		dispatch('close');
	};

	const onCloseRatingModalAfterSubmit = () => {
		isRateUsClicked.set(false);
		dispatch('close');
	};

	const maybeLater = async () => {
		const res = await handleUserGivenRating({
			[platform]: {
				show: true,
				condition: 'premium',
				ts: Date.now(),
				userAction: 'later',
			},
		});
		updateHomeData('later');
		isRateUsClicked.set(false);
		dispatch('close');
	};

	const onSubmitRatingModal = async () => {
		isSubmittedRatingLoading = true;
		updateHomeData(rating >= 4 ? 'tostore' : 'submit');
		const res = await handleUserGivenRating({
			[platform]: {
				show: false,
				condition: 'premium',
				ts: Date.now(),
				userAction: rating >= 4 ? 'tostore' : 'submit',
				stars: rating,
				message: textareaValue,
			},
		});

		if (res) {
			submittedRating = true;
			if (rating >= 4) {
				if ($app) {
					if ($os === 'IOS') {
						open_external_link(
							'https://apps.apple.com/us/app/shelly-smart-control/id1660045967?mt=8&action=write-review',
						);
					} else {
						open_external_link_android(
							'https://play.google.com/store/apps/details?id=cloud.shelly.smartcontrol',
						);
					}
				}
			}
		}

		isSubmittedRatingLoading = false;
	};

	const closeModal = (e: CustomEvent) => {
		if (showModal) {
			e.preventDefault();
			onCloseRatingModal();
		}
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModal);

			// Show new Native intent for Android only
			if (!showModal && $os !== NativeOperationSystem.IOS && checkIfNativePopUpIsOnlyModal()) {
				showNativeAndroidIntent();
			}

			return () => mobile.removeEventListener('back_button', closeModal);
		}
	});

	const checkIfNativePopUpIsOnlyModal = () => {
		const store = get(modalStore);

		if (store.length > 2) {
			return false;
		}

		if (store.length === 1) {
			return store[0].id === 'ratePromptState';
		}

		const modalIds = store.map((modal) => modal.id);

		return modalIds.includes('ratePromptState') && modalIds.includes('bluetoothScanState');
	};

	const showNativeAndroidIntent = async () => {
		await updateHomeData();
		showAndroidRateAppPopUp('inAppReview');
	};

	const updateHomeData = async (status = 'shown') => {
		const homeData = get(home_data);
		const platform = get(os);
		const activeCampaign = homeData?.ar_campaigns?.find((campaign) => campaign.id === campaignId);

		if (!activeCampaign) {
			console.error('Something is terribly wrong. Active campaign not found!');
		}

		activeCampaign[platform] = {
			status,
			seen_ts: Math.floor(Date.now() / 1000),
		};

		await postReq('/interface/home/save', {
			id: 'ar_campaigns',
			data: JSON.stringify(homeData.ar_campaigns),
		});
	};
</script>

<Modal
	on:close={onCloseRatingModal}
	{showModal}
	title={capFirstChar(
		submittedRating
			? $format('modal_title_submitted_rating', {
					default: 'Review submitted successfully',
				})
			: $format('modal_title_rating', {
					default: 'Leave feedback',
				}),
	)}
	backdropClose={true}
	hideSlotFooter={submittedRating}
	removeScrollPadding
	zIndex={priority}
>
	<Container slot="content" padding="scrollPaddings">
		{#if !submittedRating}
			<div class="mb-m d-flex flex-direction-column align-items-left">
				<Text element="h2" styleType="h2">
					{capFirstChar(
						$format('text_rating_prompt', {
							default: 'Please rate your experience',
						}),
					)}
				</Text>
				<Rating
					disabled={isSubmittedRatingLoading}
					bind:rating
					lowestRatingText={capFirstChar(
						$format('text_rating_prompt_lowest_rating', {
							default: 'poor',
						}),
					)}
					highestRatingText={capFirstChar(
						$format('text_rating_prompt_highest_rating', {
							default: 'excellent',
						}),
					)}
					class="width-full mt-s "
				/>
				<!-- <TransitionWrapper>
					{#if rating > 0 && rating <= 3} -->
				<Text class="mt-m mb-s" element="h2" styleType="h2">
					{capFirstChar(
						$format('text_rating_prompt_feedback', {
							default: 'What can we do better?',
						}),
					)}
				</Text>

				<Textarea
					disabled={isSubmittedRatingLoading}
					resize="auto"
					bind:value={textareaValue}
					class="width-full"
					size="m"
					maxChar={300}
					maxCharText={$format('text_rating_prompt_max_char', {
						default: 'characters',
					})}
					id="textarea-vk7nsfs"
					aria-label={$format('textarea_rating_feedback', {
						default: 'Textarea rating feedback',
					})}
				/>
				<!-- {/if}
				</TransitionWrapper> -->
			</div>

			<Button
				loading={isSubmittedRatingLoading}
				class="mb-s width-md-m width-sm-full"
				on:click={onSubmitRatingModal}
				disabled={showRatingModal && !rating}
			>
				{capFirstChar(
					isSubmittedRatingLoading
						? $format('button_loading_submit', { default: 'submitting' })
						: $format('button_submit', { default: 'Submit' }),
				)}
			</Button>
			{#if !$isRateUsClicked}
				<Button
					variant="secondary"
					class="mb-m width-md-m width-sm-full mr-s"
					on:click={maybeLater}
				>
					{capFirstChar($format('button_maybe_later', { default: 'maybe later' }))}
				</Button>

				<Text
					element="div"
					class="d-flex link-styles justify-content-center text-small$"
					on:click={dontAskAgain}
				>
					{capFirstChar(
						$format('button_dont_ask', {
							default: "don't ask again",
						}),
					)}
				</Text>
			{/if}
		{:else}
			<div class="d-flex flex-direction-column justify-content-center align-items-center">
				<Icon icon="fa-light fa-thumbs-up" class="text-success mb-s" size="xxl" />
				<Text element="h1" class="mb-m text-center">
					{capFirstChar(
						$format('text_rating_prompt_thank_you', {
							default: 'Thank you for your feedback!',
						}),
					)}
				</Text>
				<Text styleType="body" class="text-color-muted mt-s mb-m text-center">
					{capFirstChar(
						$format('text_rating_prompt_thank_you_description', {
							default: 'Your feedback helps us improve our products and services.',
						}),
					)}
				</Text>
				<Button on:click={onCloseRatingModalAfterSubmit}
					>{capFirstChar(
						$format('button_got_it', {
							default: 'Okay, got it!',
						}),
					)}
				</Button>
			</div>
			<!-- <div
				class="d-flex flex-direction-column justify-content-center align-items-center"
			>
				{#if rating <= 3}
					<Icon
						icon="fa-thin fa-thumbs-up"
						class="text-success mb-m"
						size="l"
					/>
					<Text element="h1" class="text-center">
						{capFirstChar(
							$format('text_rating_prompt_thank_you', {
								default: 'Thank you for your feedback!'
							})
						)}
					</Text>
					<Text styleType="body" class="text-color-muted mt-s mb-m text-center">
						{capFirstChar(
							$format('text_rating_prompt_thank_you_description', {
								default:
									'Your feedback helps us improve our products and services.'
							})
						)}
					</Text>
					<Button on:click={onCloseRatingModalAfterSubmit}
						>{capFirstChar(
							$format('button_close', {
								default: 'Close'
							})
						)}
					</Button>
				{:else}
					<Icon
						icon="fa-thin fa-thumbs-up"
						class="text-success mb-m"
						size="l"
					/>
					<Text element="h1" class="text-center">
						{capFirstChar(
							$format('text_rating_prompt_thank_you_high_rate', {
								default: 'Thank you! Please rate us on the app store as well.'
							})
						)}
					</Text>
					<Text styleType="body" class="text-color-muted mt-s mb-m text-center">
						{capFirstChar(
							$format('text_rating_prompt_thank_you_description', {
								default:
									'Your feedback in the app store will helps us improve our products and services.'
							})
						)}
					</Text>
					<Button variant="secondary" class="mb-m" on:click={onRateInStore}
						>{capFirstChar(
							$format('button_to_the_store', {
								default: 'to the app store'
							})
						)}
					</Button>
					<Text
						element="div"
						class="d-flex link-styles justify-content-center text-small$"
						on:click={onCloseRatingModalAfterSubmit}
					>
						{capFirstChar(
							$format('button_maybe_later', { default: 'maybe later' })
						)}
					</Text>
				{/if}
			</div> -->
		{/if}
	</Container>

	<!-- <div slot="footer">
		{#if !submittedRating}
			<div class="d-flex justify-content-end">
				<Button
					variant="secondary"
					class="width-md-m width-sm-full mr-s"
					responsiveSize={{
						sm: 'm'
					}}
					on:click={onCloseRatingModal}
				>
					{capFirstChar(
						$format('button_cancel', {
							default: 'Cancel'
						})
					)}
				</Button>
				<Button
					loading={isSubmittedRatingLoading}
					class="width-md-m width-sm-full"
					responsiveSize={{
						sm: 'm'
					}}
					on:click={onSubmitRatingModal}
					disabled={showRatingModal && !rating}
				>
					{capFirstChar(
						isSubmittedRatingLoading
							? $format('button_loading_submit', { default: 'submitting' })
							: $format('button_submit', { default: 'Submit' })
					)}
				</Button>
			</div>
		{/if}
	</div> -->
</Modal>
